import React, { useCallback, useEffect, useRef, useState } from "react";
import "./CotizacionesPanel.css";

const fmt = (v) => v >= 1e6 ? `${(v / 1e6).toFixed(v < 1e7 ? 1 : 0)}M` : `${Math.round(v / 1e3)}k`;

// Espeja normalizarCotizacion() del backend (lib/cotizacion.js): de 50M para arriba los valores
// van de a 5. Se aplica también acá para que el input no te deje un 87 escrito mientras el
// servidor guardó 85 — el número que ves y el que queda guardado tienen que ser el mismo.
const UMBRAL_M = 50;
const PASO_M = 5;
const normalizarM = (m) => {
    const n = parseFloat(m);
    if (!isFinite(n) || n < 0) return 0;
    if (n < UMBRAL_M) return Math.round(n * 10) / 10;
    return Math.round(n / PASO_M) * PASO_M;
};

/**
 * Panel de cotizaciones: la tabla de valores del juego "Fichaje o No Fichaje", del más caro al
 * más barato, con filtros y edición en línea.
 *
 * El valor vive en Player.cotizacion. Al editarlo se marca cotizacion_manual, y el recálculo
 * automático (scripts/backfillCotizaciones.js) deja de pisarlo.
 */
export default function CotizacionesPanel({ adminFetch }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [paginas, setPaginas] = useState(1);
    const [page, setPage] = useState(1);
    const [orden, setOrden] = useState("desc");
    const [cargando, setCargando] = useState(false);
    const [msg, setMsg] = useState(null);

    const [filtros, setFiltros] = useState({ clubes: [], logros: [] });
    const [q, setQ] = useState("");
    const [club, setClub] = useState("");
    const [logros, setLogros] = useState([]);
    const [min, setMin] = useState("");
    const [max, setMax] = useState("");
    const [soloManuales, setSoloManuales] = useState(false);

    const [edicion, setEdicion] = useState({});   // id -> texto del input
    const [pendiente, setPendiente] = useState(null); // id con guardado programado
    const timers = useRef({});
    // Espejo del valor en edición. Hace falta porque React agrupa los setState: dos clics
    // seguidos en +5 leían el mismo valor viejo del estado y sumaban 5 en vez de 10.
    const valores = useRef({});

    // Limpia los timers al desmontar: si no, un autoguardado programado dispara sobre un panel
    // que ya no existe.
    useEffect(() => {
        const t = timers.current;
        return () => Object.values(t).forEach(clearTimeout);
    }, []);

    useEffect(() => {
        adminFetch("/players/cotizaciones/filtros")
            .then(setFiltros)
            .catch(e => setMsg({ tipo: "error", texto: e.message }));
    }, [adminFetch]);

    const cargar = useCallback(async (paginaPedida = page) => {
        setCargando(true);
        try {
            const p = new URLSearchParams({ page: paginaPedida, limit: 50, orden });
            if (q) p.set("q", q);
            if (club) p.set("club", club);
            if (logros.length) p.set("logros", logros.join(","));
            if (min !== "") p.set("min", min);
            if (max !== "") p.set("max", max);
            if (soloManuales) p.set("soloManuales", "1");
            const d = await adminFetch(`/players/cotizaciones?${p}`);
            setItems(d.items); setTotal(d.total); setPaginas(d.paginas); setPage(d.page);
            setEdicion({}); valores.current = {};
        } catch (e) {
            setMsg({ tipo: "error", texto: e.message });
        } finally {
            setCargando(false);
        }
    }, [adminFetch, page, orden, q, club, logros, min, max, soloManuales]);

    // Los filtros de seleccion recargan solos; el texto y los rangos esperan al boton Filtrar.
    // `cargar` queda fuera de las dependencias a proposito: cambia en cada render y volveria a
    // pedir la pagina en loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargar(1); }, [orden, club, logros, soloManuales]);

    const guardar = async (p, valorCrudo) => {
        const valor = valorCrudo === "" || valorCrudo === null || valorCrudo === undefined
            ? "" : String(normalizarM(valorCrudo));
        clearTimeout(timers.current[p._id]);
        delete timers.current[p._id];
        delete valores.current[p._id];
        setPendiente(x => (x === p._id ? null : x));
        try {
            const d = await adminFetch(`/players/${p._id}/cotizacion`, {
                method: "PUT",
                body: JSON.stringify({ valor: valor === "" ? null : Number(valor) }),
            });
            setItems(prev => prev.map(x => x._id === p._id
                ? { ...x, cotizacion: d.cotizacion, manual: d.manual } : x));
            setEdicion(e => { const c = { ...e }; delete c[p._id]; return c; });
            setMsg({ tipo: "ok", texto: `${d.nombre}: ${fmt(d.cotizacion)}${d.manual ? " (fijado a mano)" : " (automático)"}` });
        } catch (e) {
            setMsg({ tipo: "error", texto: e.message });
        }
    };

    /**
     * Programa el guardado 3 segundos después del último toque sobre ESE jugador. Con los
     * botones ±5 se encadenan varios clics seguidos, y tener que apretar Guardar cada vez hacía
     * el ajuste fino insoportable. Cada nuevo toque reinicia el reloj.
     */
    const programarGuardado = (p, valor) => {
        valores.current[p._id] = valor;
        setEdicion(e => ({ ...e, [p._id]: valor }));
        setPendiente(p._id);
        clearTimeout(timers.current[p._id]);
        timers.current[p._id] = setTimeout(() => guardar(p, valor), 3000);
    };

    /** ±5M. Nunca baja de 0 y el backend redondea igual de 50M para arriba. */
    const ajustar = (p, delta) => {
        const base = valores.current[p._id] ?? edicion[p._id] ?? (p.cotizacion / 1e6);
        // Se normaliza la base primero: partiendo de 52, +5 tiene que dar 55 y no 57.
        const actual = normalizarM(base);
        const nuevo = Math.max(0, normalizarM(actual + delta));
        valores.current[p._id] = nuevo;
        programarGuardado(p, String(nuevo));
    };

    const toggleLogro = (l) => setLogros(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);

    return (
        <div className="cot-panel">
            <div className="cot-filtros">
                <form className="cot-busqueda" onSubmit={(e) => { e.preventDefault(); cargar(1); }}>
                    <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre…" />
                    <select value={club} onChange={e => setClub(e.target.value)}>
                        <option value="">Todos los clubes</option>
                        {filtros.clubes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input className="cot-num" type="number" value={min} onChange={e => setMin(e.target.value)} placeholder="min M" />
                    <input className="cot-num" type="number" value={max} onChange={e => setMax(e.target.value)} placeholder="máx M" />
                    <select value={orden} onChange={e => setOrden(e.target.value)}>
                        <option value="desc">Mayor valor primero</option>
                        <option value="asc">Menor valor primero</option>
                    </select>
                    <label className="cot-check">
                        <input type="checkbox" checked={soloManuales} onChange={e => setSoloManuales(e.target.checked)} />
                        Solo fijados a mano
                    </label>
                    <button type="submit" className="cot-btn">Filtrar</button>
                </form>

                <div className="cot-logros">
                    {filtros.logros.map(l => (
                        <button key={l}
                            className={`cot-chip${logros.includes(l) ? " activo" : ""}`}
                            onClick={() => toggleLogro(l)}>{l}</button>
                    ))}
                </div>
            </div>

            {msg && <p className={`cot-msg cot-msg--${msg.tipo}`} onClick={() => setMsg(null)}>{msg.texto}</p>}

            <div className="cot-resumen">
                <span>{total} jugadores</span>
                <span>página {page} de {paginas || 1}</span>
            </div>

            <div className="cot-tabla-wrap">
                <table className="cot-tabla">
                    <thead>
                        <tr>
                            <th></th><th>Jugador</th><th>Cotización</th><th></th>
                            <th>País</th><th>Puestos</th><th>Títulos</th><th>Sugerida</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(p => {
                            const editando = edicion[p._id] !== undefined;
                            const valorInput = editando ? edicion[p._id] : (p.cotizacion / 1e6).toString();
                            return (
                                <tr key={p._id} className={p.manual ? "manual" : ""}>
                                    <td>{p.img && <img src={p.img} alt="" className="cot-foto" />}</td>
                                    <td className="cot-nombre">{p.nombre}</td>
                                    <td>
                                      <div className="cot-celda-valor">
                                        <button className="cot-paso" title="-5M"
                                            onClick={() => ajustar(p, -5)}>−5</button>
                                        <input className="cot-valor" type="number"
                                            step={parseFloat(valorInput) >= UMBRAL_M ? PASO_M : 0.1}
                                            value={valorInput}
                                            onChange={e => programarGuardado(p, e.target.value)}
                                            onBlur={e => {
                                                // Snap al salir del campo: mientras escribís te deja
                                                // libre, pero no te podés ir con un 87 tipeado.
                                                const n = String(normalizarM(e.target.value));
                                                if (n !== e.target.value) programarGuardado(p, n);
                                            }}
                                            onKeyDown={e => e.key === "Enter" && guardar(p, e.target.value)} />
                                        <button className="cot-paso" title="+5M"
                                            onClick={() => ajustar(p, +5)}>+5</button>
                                        <span className="cot-unidad">M</span>
                                        {pendiente === p._id && <span className="cot-pendiente">guardando…</span>}
                                        {p.manual && pendiente !== p._id && <span className="cot-badge">manual</span>}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="cot-acciones">
                                        <button className="cot-btn" disabled={!editando}
                                            title="Se guarda solo a los 3 segundos; esto lo fuerza ya"
                                            onClick={() => guardar(p, edicion[p._id])}>Guardar</button>
                                        {p.manual && (
                                            <button className="cot-btn cot-btn--ghost" title="Volver al valor automático"
                                                onClick={() => guardar(p, "")}>Auto</button>
                                        )}
                                      </div>
                                    </td>
                                    <td>{p.pais}</td>
                                    <td className="cot-pos">{p.posiciones.join(", ")}</td>
                                    <td className="cot-titulos">{p.titulos.length ? p.titulos.join(", ") : "—"}</td>
                                    <td className="cot-sugerida">{fmt(p.sugerida)}</td>
                                </tr>
                            );
                        })}
                        {!items.length && !cargando && (
                            <tr><td colSpan={8} className="cot-vacio">Sin resultados con esos filtros.</td></tr>
                        )}
                    </tbody>
                </table>
                {cargando && <p className="cot-cargando">Cargando…</p>}
            </div>

            <div className="cot-paginado">
                <button className="cot-btn" disabled={page <= 1} onClick={() => cargar(page - 1)}>← Anterior</button>
                <span>{page} / {paginas || 1}</span>
                <button className="cot-btn" disabled={page >= paginas} onClick={() => cargar(page + 1)}>Siguiente →</button>
            </div>
        </div>
    );
}
